import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import { AuthContext } from '../../contexts/AuthContext'; // Ensure this path is correct
import { Form, Row, Col, Button, ListGroup, Accordion, Spinner } from 'react-bootstrap';
import { Hospital, Trash2, Plus, ChevronDown, ChevronUp, ToggleLeft, ToggleRight, MapPin } from 'lucide-react';

const defaultFacilityShape = {
  id: null,
  name: '',
  location: '',
  contactPerson: { name: '', email: '', phone: '' },
  facilityDetails: { facilityType: '' },
  clinicalExpectations: { emrSystem: '' },
  useGroupContact: false,
};

const FacilityDetailsStep = ({ 
  facilities: facilitiesFromProps = [], 
  updateFacility: updateFacilityProp,
  addFacility: addFacilityProp, 
  removeFacility: removeFacilityProp,
  // errors prop from parent might not be directly used by RHF for field errors
  group = {} // Group data for default contact info
}) => {
  const { auth } = useContext(AuthContext);
  const hasAuthToken = !!auth?.token;
  
  // We're not using facility context directly to avoid hook issues

  const {
    register,
    control,
    setValue,
    reset,
    watch,
    formState: { errors: formErrors }
  } = useForm({
    defaultValues: {
      facilities: []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'facilities'
  });

  // Local state for UI interactions not directly tied to RHF field values
  const [locationSearch, setLocationSearch] = useState(''); // For location search text
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [activeLocationFacilityIndex, setActiveLocationFacilityIndex] = useState(null); // Index of the facility whose location field is being edited
  const [isLoadingLocationSuggestions, setIsLoadingLocationSuggestions] = useState(false);
  
  const [collapsedFacilities, setCollapsedFacilities] = useState({}); // For accordion UI state

  const locationRef = useRef(null); // Ref for the location input container to detect outside clicks
  const programmaticRHFUpdateRef = useRef(null); // Shield against prop-driven RHF updates during selection
  const userInputTracker = useRef({}); // Track user inputs to prevent overwriting by prop updates
  
  const facilityTypes = [
    { value: 'hospital', label: 'Hospital' },
    { value: 'surgery center', label: 'Surgery Center' },
    { value: 'office based', label: 'Office-Based' },
    { value: 'pain clinic', label: 'Pain Clinic' },
    { value: 'other', label: 'Other' }
  ];
  
  const commonEmrSystems = [
    'Epic', 'Cerner', 'Meditech', 'Allscripts', 'NextGen', 
    'eClinicalWorks', 'Centricity', 'athenahealth'
  ];
  
  const toggleFacilityCollapse = (facilityIndex) => {
    setCollapsedFacilities(prevState => ({
      ...prevState,
      [facilityIndex]: !prevState[facilityIndex]
    }));
  };

  const isFacilityCollapsed = (facilityIndex) => {
    return collapsedFacilities[facilityIndex] === true;
  };
  
  const updateFacilityFieldImmediately = useCallback((facilityIndex, fieldName, value) => {
    if (!hasAuthToken) {
      console.warn('FacilityDetailsStep: No auth token, skipping immediate update for', fieldName);
      return;
    }
    if (updateFacilityProp) {
      console.log(`FacilityDetailsStep: Immediate update for facility index ${facilityIndex}, field ${fieldName}:`, value);
      updateFacilityProp(facilityIndex, fieldName, value);
    } else {
      console.warn('FacilityDetailsStep: updateFacilityProp function is not available for immediate update.');
    }
  }, [hasAuthToken, updateFacilityProp]);

  const debouncedUpdateFacilityField = useDebouncedCallback((facilityIndex, fieldName, value) => {
    if (!hasAuthToken) {
      console.warn('FacilityDetailsStep: No auth token, skipping debounced update for', fieldName);
      return;
    }
    if (updateFacilityProp) {
      console.log(`FacilityDetailsStep: Debounced update for facility index ${facilityIndex}, field ${fieldName}:`, value);
      updateFacilityProp(facilityIndex, fieldName, value);
    } else {
      console.warn('FacilityDetailsStep: updateFacilityProp function is not available for debounced update.');
    }
  }, 1000);

  const handleRHFInputChange = useCallback((facilityIndex, fieldName, fieldValue, options = {}) => {
    const { isImmediate = false } = options;
    const fullRHFFieldName = `facilities.${facilityIndex}.${fieldName}`;
    setValue(fullRHFFieldName, fieldValue, { shouldValidate: true, shouldDirty: true });

    // Track this user input to prevent it from being overwritten by the props sync
    if (!userInputTracker.current[facilityIndex]) {
      userInputTracker.current[facilityIndex] = {};
    }
    userInputTracker.current[facilityIndex][fieldName] = {
      value: fieldValue,
      timestamp: Date.now()
    };

    // Special handling for facilityType and emrSystem - these need to be at root level for the backend
    if (fieldName === 'facilityDetails.facilityType') {
      // For facility type, also set it at the root level
      const rootFieldName = 'facilityType';
      const rootFullRHFFieldName = `facilities.${facilityIndex}.${rootFieldName}`;
      
      // Update the RHF field
      setValue(rootFullRHFFieldName, fieldValue, { shouldValidate: true, shouldDirty: true });
      
      // Track the user input
      userInputTracker.current[facilityIndex][rootFieldName] = {
        value: fieldValue,
        timestamp: Date.now()
      };
      
      // Only send the root-level update to the API
      if (isImmediate) {
        updateFacilityFieldImmediately(facilityIndex, rootFieldName, fieldValue);
      } else {
        debouncedUpdateFacilityField(facilityIndex, rootFieldName, fieldValue);
      }
      return; // Skip the rest of the handling
    }
    else if (fieldName === 'clinicalExpectations.emrSystem') {
      // For EMR system, also set it at the root level
      const rootFieldName = 'emrSystem';
      const rootFullRHFFieldName = `facilities.${facilityIndex}.${rootFieldName}`;
      
      // Update the RHF field
      setValue(rootFullRHFFieldName, fieldValue, { shouldValidate: true, shouldDirty: true });
      
      // Track the user input
      userInputTracker.current[facilityIndex][rootFieldName] = {
        value: fieldValue,
        timestamp: Date.now()
      };
      
      // Only send the root-level update to the API
      if (isImmediate) {
        updateFacilityFieldImmediately(facilityIndex, rootFieldName, fieldValue);
      } else {
        debouncedUpdateFacilityField(facilityIndex, rootFieldName, fieldValue);
      }
      return; // Skip the rest of the handling
    }
    // Special handling for nested contactPerson fields
    else if (fieldName.startsWith('contactPerson.')) {
      // For contact person fields, we need to send the entire contact object
      const contactPersonFields = watch(`facilities.${facilityIndex}.contactPerson`);
      // Update the specific field in our local copy
      const fieldParts = fieldName.split('.');
      const updatedContactPerson = {
        ...contactPersonFields,
        [fieldParts[1]]: fieldValue // Index 1 is the actual field name after 'contactPerson.'
      };
      
      // Send the complete contact person object to the parent
      if (isImmediate) {
        updateFacilityFieldImmediately(facilityIndex, 'contactPerson', updatedContactPerson);
      } else {
        debouncedUpdateFacilityField(facilityIndex, 'contactPerson', updatedContactPerson);
      }
    }
    // Special handling for nested facilityDetails fields (except facilityType which is handled above)
    else if (fieldName.startsWith('facilityDetails.') && fieldName !== 'facilityDetails.facilityType') {
      // Similar approach for facility details
      const facilityDetailsFields = watch(`facilities.${facilityIndex}.facilityDetails`);
      const fieldParts = fieldName.split('.');
      const updatedFacilityDetails = {
        ...facilityDetailsFields,
        [fieldParts[1]]: fieldValue
      };
      
      if (isImmediate) {
        updateFacilityFieldImmediately(facilityIndex, 'facilityDetails', updatedFacilityDetails);
      } else {
        debouncedUpdateFacilityField(facilityIndex, 'facilityDetails', updatedFacilityDetails);
      }
    }
    // Special handling for nested clinicalExpectations fields (except emrSystem which is handled above)
    else if (fieldName.startsWith('clinicalExpectations.') && fieldName !== 'clinicalExpectations.emrSystem') {
      // Similar approach for clinical expectations
      const clinicalExpectationsFields = watch(`facilities.${facilityIndex}.clinicalExpectations`);
      const fieldParts = fieldName.split('.');
      const updatedClinicalExpectations = {
        ...clinicalExpectationsFields,
        [fieldParts[1]]: fieldValue
      };
      
      if (isImmediate) {
        updateFacilityFieldImmediately(facilityIndex, 'clinicalExpectations', updatedClinicalExpectations);
      } else {
        debouncedUpdateFacilityField(facilityIndex, 'clinicalExpectations', updatedClinicalExpectations);
      }
    }
    // Standard handling for non-nested fields
    else {
      if (isImmediate) {
        updateFacilityFieldImmediately(facilityIndex, fieldName, fieldValue);
      } else {
        debouncedUpdateFacilityField(facilityIndex, fieldName, fieldValue);
      }
    }
  }, [setValue, watch, debouncedUpdateFacilityField, updateFacilityFieldImmediately]);

  const handleLocationInputFocus = (index) => {
    setActiveLocationFacilityIndex(index);
    const rhfValue = watch(`facilities.${index}.location`) || '';
    setLocationSearch(rhfValue);
    setShowLocationSuggestions(true);
  };

  const fetchLocationSuggestions = useDebouncedCallback(async (searchText) => {
    if (!searchText || searchText.length < 3) {
      setLocationSuggestions([]);
      setIsLoadingLocationSuggestions(false);
      return;
    }
    
    try {
      setIsLoadingLocationSuggestions(true);
      
      // Use OpenStreetMap Nominatim API (free, no API key needed)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchText)}&countrycodes=us&limit=5&addressdetails=1`,
        { headers: { 'Accept-Language': 'en-US,en' } }
      );
      
      if (!response.ok) throw new Error('Network response was not ok');
      
      const data = await response.json();
      
      // Format suggestions to show city, state
      const formattedResults = data.map(item => {
        const city = item.address.city || item.address.town || item.address.village || item.address.hamlet || '';
        const state = item.address.state || '';
        return {
          display: city && state ? `${city}, ${state}` : item.display_name.split(',').slice(0, 2).join(','),
          raw: item
        };
      });
      
      setLocationSuggestions(formattedResults);
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
      setLocationSuggestions([]);
    } finally {
      setIsLoadingLocationSuggestions(false);
    }
  }, 400);
  
  const handleLocationSearchChange = (index, value) => {
    if (index === activeLocationFacilityIndex) {
      setLocationSearch(value);
      setShowLocationSuggestions(true);
      fetchLocationSuggestions(value);
    }
  };
  
  const selectLocationValue = (index, suggestion) => {
    if (index === activeLocationFacilityIndex) {
      const fieldPath = `facilities.${index}.location`;
      const selectedValue = suggestion.display;
      
      // Set the search value immediately
      setLocationSearch(selectedValue);
      
      // Update the RHF field
      setValue(fieldPath, selectedValue, { shouldValidate: true, shouldDirty: true });
      
      // Track this user input to prevent overwrite
      if (!userInputTracker.current[index]) {
        userInputTracker.current[index] = {};
      }
      userInputTracker.current[index]['location'] = {
        value: selectedValue,
        timestamp: Date.now()
      };
      
      // Shield against potential circular updates
      programmaticRHFUpdateRef.current = { path: fieldPath, selectedValue: selectedValue };
      
      // Update parent component/backend
      if (updateFacilityProp) {
        updateFacilityProp(index, 'location', selectedValue);
      }
      
      // Hide suggestions
      setShowLocationSuggestions(false);
      setLocationSuggestions([]);
      
      // Important: We're NOT clearing the activeLocationFacilityIndex immediately
      // This keeps showing the selected value in the input until the next render cycle
      
      // Clear active index after ensuring React Hook Form state is updated
      setTimeout(() => {
        setActiveLocationFacilityIndex(null);
      }, 300);
    }
  };

  const handleToggleUseGroupContactRHF = (index) => {
    const currentVal = watch(`facilities.${index}.useGroupContact`);
    // Toggle and persist immediately
    handleRHFInputChange(index, 'useGroupContact', !currentVal, { isImmediate: true });
  };
  
  // Initialize useGroupContact when facilities change
  useEffect(() => {
    const rhfFacilities = watch('facilities');
    const now = Date.now();
    const PROTECTION_WINDOW_MS = 5000; // Protect user inputs for 5 seconds

    // Case 1: Array lengths are different, typically when adding or removing facilities
    if (facilitiesFromProps.length !== rhfFacilities.length) {
      const formValuesForReset = facilitiesFromProps.map((facility, idx) => {
        // Check if there's tracked user input for this facility
        // For new facilities, we must inspect user inputs carefully
        const hasTrackedInputs = userInputTracker.current[idx];
        
        const baseValues = {
          ...defaultFacilityShape,
          ...facility,
          contactPerson: { ...defaultFacilityShape.contactPerson, ...(facility.contactPerson || {}) },
          facilityDetails: { ...defaultFacilityShape.facilityDetails, ...(facility.facilityDetails || {}) },
          clinicalExpectations: { ...defaultFacilityShape.clinicalExpectations, ...(facility.clinicalExpectations || {}) },
          useGroupContact: facility.useGroupContact !== undefined ? facility.useGroupContact : (
            !facility.contactPerson?.name &&
            !facility.contactPerson?.email &&
            !facility.contactPerson?.phone &&
            !!group.contact?.name
          )
        };
        
        // Ensure facilityType is set at root level if it exists in facilityDetails
        if (facility.facilityDetails?.facilityType && !facility.facilityType) {
          baseValues.facilityType = facility.facilityDetails.facilityType;
        }
        // Or vice versa - if it's at root level but not in facilityDetails
        else if (facility.facilityType && !facility.facilityDetails?.facilityType) {
          if (!baseValues.facilityDetails) baseValues.facilityDetails = {};
          baseValues.facilityDetails.facilityType = facility.facilityType;
        }
        
        // If this is a new facility or one without tracked inputs, return baseValues
        if (!hasTrackedInputs) return baseValues;
        
        // Otherwise, preserve user inputs if they're recent
        const preservedValues = { ...baseValues };
        Object.entries(userInputTracker.current[idx]).forEach(([fieldKey, { value, timestamp }]) => {
          // If the input is recent (within the protection window), preserve it
          if (now - timestamp < PROTECTION_WINDOW_MS) {
            if (fieldKey.includes('.')) {
              // Handle nested fields like contactPerson.name
              const [parentKey, childKey] = fieldKey.split('.');
              if (!preservedValues[parentKey]) preservedValues[parentKey] = {};
              preservedValues[parentKey][childKey] = value;
            } else {
              // Handle top-level fields like name
              preservedValues[fieldKey] = value;
            }
          }
        });
        
        return preservedValues;
      });
      
      reset({ facilities: formValuesForReset });
    } 
    // Case 2: Same number of facilities, just updating fields
    else {
      facilitiesFromProps.forEach((facilityFromProp, index) => {
        const rhfFacilityAtIndex = rhfFacilities[index];
        if (!rhfFacilityAtIndex) return;

        Object.keys(facilityFromProp).forEach(key => {
          const propValue = facilityFromProp[key];
          const fieldPath = `facilities.${index}.${key}`;
          
          // Check if this field has recent user input
          const userInput = userInputTracker.current[index] && userInputTracker.current[index][key];
          const isRecentUserInput = userInput && (now - userInput.timestamp < PROTECTION_WINDOW_MS);
          
          // Skip synchronization for fields with recent user input
          if (isRecentUserInput) {
            console.log(`Preserving user input for ${fieldPath} (${userInput.value})`);
            return;
          }

          if (typeof propValue === 'object' && propValue !== null && !Array.isArray(propValue)) {
            Object.keys(propValue).forEach(subKey => {
              const propSubValue = propValue[subKey];
              const subFieldPath = `facilities.${index}.${key}.${subKey}`;
              const nestedUserInput = userInputTracker.current[index] && 
                                    userInputTracker.current[index][`${key}.${subKey}`];
              const isRecentNestedUserInput = nestedUserInput && 
                                            (now - nestedUserInput.timestamp < PROTECTION_WINDOW_MS);
              
              // Skip synchronization for nested fields with recent user input
              if (isRecentNestedUserInput) {
                console.log(`Preserving nested user input for ${subFieldPath} (${nestedUserInput.value})`);
                return;
              }
              
              if (JSON.stringify(propSubValue) !== JSON.stringify(watch(subFieldPath))) {
                setValue(subFieldPath, propSubValue, { shouldDirty: false, shouldValidate: false });
              }
            });
          } else {
            const activeShield = programmaticRHFUpdateRef.current;

            if (activeShield && activeShield.path === fieldPath) {
              // Shield is active for this specific field.
              if (JSON.stringify(propValue) === JSON.stringify(activeShield.selectedValue)) {
                // Prop has caught up to the value that was selected.
                programmaticRHFUpdateRef.current = null;
                const currentRHFValue = watch(fieldPath);
                if (JSON.stringify(currentRHFValue) !== JSON.stringify(propValue)) {
                  setValue(fieldPath, propValue, { shouldDirty: false, shouldValidate: false });
                }
              }
            } else {
              // Normal synchronization if no shield is active.
              const currentRHFValue = watch(fieldPath);
              if (JSON.stringify(propValue) !== JSON.stringify(currentRHFValue)) {
                setValue(fieldPath, propValue, { shouldDirty: false, shouldValidate: false });
              }
            }
          }
        });

        // Handle useGroupContact toggle separately
        const calculatedUseGroupContact = facilityFromProp.useGroupContact !== undefined ? facilityFromProp.useGroupContact : (
          !facilityFromProp.contactPerson?.name &&
          !facilityFromProp.contactPerson?.email &&
          !facilityFromProp.contactPerson?.phone &&
          !!group.contact?.name
        );
        
        // Check if this field has recent user input
        const userInput = userInputTracker.current[index] && userInputTracker.current[index]['useGroupContact'];
        const isRecentUserInput = userInput && (now - userInput.timestamp < PROTECTION_WINDOW_MS);
        
        // Skip synchronization if useGroupContact has recent user input
        if (!isRecentUserInput && calculatedUseGroupContact !== rhfFacilityAtIndex.useGroupContact) {
          setValue(`facilities.${index}.useGroupContact`, calculatedUseGroupContact, { shouldDirty: false, shouldValidate: false });
        }
      });
    }
  }, [facilitiesFromProps, reset, watch, setValue, group.contact]);
  
  // EMR selection is now handled directly by handleNestedChange
  
  // We no longer need the homegrown location search useEffect - it's replaced by the fetchLocationSuggestions function

  // Click outside to close suggestions
  useEffect(() => {
    if (!showLocationSuggestions) {
      setLocationSuggestions([]);
      return;
    }
    
    const handleClickOutside = (event) => {
      if (locationRef.current && !locationRef.current.contains(event.target)) {
        setShowLocationSuggestions(false);
        if (activeLocationFacilityIndex !== null) {
          setActiveLocationFacilityIndex(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [locationRef, showLocationSuggestions, activeLocationFacilityIndex]);
  




  return (
    <div className="form-content">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="section-title mb-0">
          <Hospital size={20} />
          Facilities
        </div>
        <Button 
          className="btn-add" 
          onClick={() => {
            const newFacilityData = { ...defaultFacilityShape, id: `temp-${Date.now()}` };
            append(newFacilityData);
            if (addFacilityProp) addFacilityProp(); // Call prop if it exists
          }}
        >
          <Plus size={18} /> Add Facility
        </Button>
      </div>

      {fields.length === 0 ? (
        <div className="text-center p-5 bg-light rounded">
          <p className="mb-3 text-muted">No facilities added yet.</p>
          <Button 
            className="btn-primary" 
            onClick={() => {
              const newFacilityData = { ...defaultFacilityShape, id: `temp-${Date.now()}` };
              append(newFacilityData);
              if (addFacilityProp) addFacilityProp(); // Call prop if it exists
            }}
          >
            <Plus size={18} /> Add Your First Facility
          </Button>
        </div>
      ) : (
        fields.map((field, index) => { // 'field' is from useFieldArray
          // Individual fields are watched directly or registered, so 'facility' const is not needed here.
          return (
          <div key={field.id} className="facility-card"> {/* Use field.id for key */}
            <div 
              className="facility-header"
              onClick={() => toggleFacilityCollapse(index)}
              style={{ cursor: 'pointer' }}
            >
              <h4 className="facility-title">
                {watch(`facilities.${index}.name`) || `Facility ${index + 1}`}
              </h4>
              <div className="facility-header-actions">
                <Button 
                  variant="link" 
                  className="p-0 text-danger me-3" 
                  onClick={(e) => {
                    e.stopPropagation();
                    const facilityId = watch(`facilities.${index}.id`); // Get ID before potentially removing
                    
                    // Directly remove the facility, depending on what's available
                    if (typeof removeFacilityProp === 'function') {
                      // Use the removeFacility prop passed from the parent
                      removeFacilityProp(facilityId, index);
                    } else {
                      // If prop isn't available, directly remove from React Hook Form
                      if (window.confirm(`Are you sure you want to delete this facility?`)) {
                        remove(index);
                      }
                    }
                  }}
                >
                  <Trash2 size={18} />
                </Button>
                <div className="facility-collapse-indicator">
                  {isFacilityCollapsed(index) ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                </div>
              </div>
            </div>
            
            <Accordion activeKey={isFacilityCollapsed(index) ? '' : String(index)}>
              <Accordion.Collapse eventKey={String(index)}>
                <div className="facility-content">
                  <Form.Group className="field-group">
                    <Form.Label className="field-label">Facility Name *</Form.Label>
                    <Form.Control
                      type="text"
                      className="facility-name-input"
                      placeholder="Enter Facility Name"
                      {...register(`facilities.${index}.name`, { required: 'Facility Name is required' })}
                      onChange={(e) => handleRHFInputChange(index, 'name', e.target.value)}
                      isInvalid={!!formErrors.facilities?.[index]?.name}
                    />
                    {formErrors.facilities?.[index]?.name && (
                      <Form.Text className="text-danger field-error">
                        {formErrors.facilities[index].name.message}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="field-group">
                    <Form.Label className="field-label">Location *</Form.Label>
                    <div ref={locationRef} style={{ position: 'relative' }}>
                      <div className="input-with-icon">
                        <Form.Control
                          type="text"
                          placeholder="Search for a city (e.g., Miami, FL)"
                          value={activeLocationFacilityIndex === index ? locationSearch : (watch(`facilities.${index}.location`) || '')}
                          onFocus={() => handleLocationInputFocus(index)}
                          onChange={(e) => handleLocationSearchChange(index, e.target.value)}
                          onBlur={() => setTimeout(() => setActiveLocationFacilityIndex(null), 200)}
                          autoComplete="off"
                        />
                        <MapPin size={18} className="search-icon" style={{ position: 'absolute', right: '10px', top: '10px', opacity: 0.5 }} />
                      </div>
                      
                      {activeLocationFacilityIndex === index && isLoadingLocationSuggestions && (
                        <div className="d-flex justify-content-center py-2">
                          <Spinner animation="border" size="sm" />
                        </div>
                      )}
                      
                      {showLocationSuggestions && activeLocationFacilityIndex === index && locationSuggestions.length > 0 && (
                        <ListGroup className="suggestions-list-group shadow-sm">
                          {locationSuggestions.map((suggestion, sIndex) => (
                            <ListGroup.Item 
                              key={sIndex} 
                              action 
                              onClick={() => selectLocationValue(index, suggestion)}
                              className="d-flex align-items-center"
                            >
                              <MapPin size={14} className="me-2" />
                              {suggestion.display}
                            </ListGroup.Item>
                          ))}
                          <ListGroup.Item className="text-center small text-muted py-1">
                            Powered by OpenStreetMap
                          </ListGroup.Item>
                        </ListGroup>
                      )}
                    </div>
                    {formErrors.facilities?.[index]?.location && <Form.Text className="text-danger">{formErrors.facilities[index].location.message}</Form.Text>}
                  </Form.Group>

                  <div className="section-title mt-4 mb-3 d-flex align-items-center justify-content-between">
                    <span>Contact Information</span>
                    <div 
                      className="d-flex align-items-center"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent accordion toggle
                        handleToggleUseGroupContactRHF(index);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="toggle-switch">
                        {watch(`facilities.${index}.useGroupContact`) ? (
                          <ToggleRight size={24} className="text-primary" />
                        ) : (
                          <ToggleLeft size={24} className="text-muted" />
                        )}
                      </div>
                      <span className="ms-2 small" style={{ whiteSpace: 'nowrap' }}>
                        {watch(`facilities.${index}.useGroupContact`) ? 'Using Group Contact' : 'Custom Contact'}
                      </span>
                    </div>
                  </div>

                  {!watch(`facilities.${index}.useGroupContact`) && (
                    <Row>
                      <Col md={4}>
                        <Form.Group className="field-group">
                          <Form.Label className="field-label">Contact Name</Form.Label>
                          <Form.Control
                            {...register(`facilities.${index}.contactPerson.name`)}
                            onChange={(e) => handleRHFInputChange(index, 'contactPerson.name', e.target.value)}
                            placeholder="Contact person"
                            className="form-control"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="field-group">
                          <Form.Label className="field-label">Email</Form.Label>
                          <Form.Control
                            type="email"
                            {...register(`facilities.${index}.contactPerson.email`)}
                            onChange={(e) => handleRHFInputChange(index, 'contactPerson.email', e.target.value)}
                            placeholder="Email address"
                            className="form-control"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="field-group">
                          <Form.Label className="field-label">Phone</Form.Label>
                          <Form.Control
                            type="tel"
                            {...register(`facilities.${index}.contactPerson.phone`)}
                            onChange={(e) => handleRHFInputChange(index, 'contactPerson.phone', e.target.value)}
                            placeholder="Phone number"
                            className="form-control"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  <div className="section-title mt-4 mb-3">
                    Facility Details
                  </div>
                  
                  <Form.Group className="field-group">
                    <Form.Label className="field-label">Facility Type</Form.Label>
                    <div className="tag-selector">
                      {facilityTypes.map((type) => (
                        <div
                          key={type.value}
                          className={`tag-pill ${(watch(`facilities.${index}.facilityDetails.facilityType`)?.toLowerCase() === type.value.toLowerCase() || watch(`facilities.${index}.facilityType`)?.toLowerCase() === type.value.toLowerCase()) ? 'selected' : ''}`}
                          onClick={() => {
                            console.log('Selecting facility type:', type.value);
                            
                            // Update React Hook Form fields - both at nested location for UI and root level for backend
                            setValue(`facilities.${index}.facilityDetails.facilityType`, type.value, { shouldValidate: true, shouldDirty: true });
                            setValue(`facilities.${index}.facilityType`, type.value, { shouldValidate: true, shouldDirty: true });
                            
                            // Track these user inputs to prevent overwriting
                            if (!userInputTracker.current[index]) {
                              userInputTracker.current[index] = {};
                            }
                            
                            // Track both paths
                            const timestamp = Date.now();
                            userInputTracker.current[index]['facilityDetails.facilityType'] = {
                              value: type.value,
                              timestamp
                            };
                            userInputTracker.current[index]['facilityType'] = {
                              value: type.value,
                              timestamp
                            };
                            
                            // Only update the root-level facilityType in the API - this is what the backend expects
                            // No need to update facilityDetails.facilityType in the API
                            updateFacilityFieldImmediately(index, 'facilityType', type.value);
                          }}
                        >
                          {type.label}
                        </div>
                      ))}
                    </div>
                  </Form.Group>
                  
                  <Form.Group className="field-group">
                    <Form.Label className="field-label">EMR System</Form.Label>
                    <div className="tag-selector">
                      {commonEmrSystems.map((emr) => (
                        <div
                          key={emr}
                          className={`tag-pill ${(watch(`facilities.${index}.clinicalExpectations.emrSystem`)?.toLowerCase() === emr.toLowerCase() || watch(`facilities.${index}.emrSystem`)?.toLowerCase() === emr.toLowerCase()) ? 'selected' : ''}`}
                          onClick={() => {
                            // Update React Hook Form fields at both nested and root levels
                            setValue(`facilities.${index}.clinicalExpectations.emrSystem`, emr, { shouldValidate: true, shouldDirty: true });
                            setValue(`facilities.${index}.emrSystem`, emr, { shouldValidate: true, shouldDirty: true });
                            
                            // Track both user inputs to prevent overwriting
                            if (!userInputTracker.current[index]) {
                              userInputTracker.current[index] = {};
                            }
                            
                            const timestamp = Date.now();
                            userInputTracker.current[index]['clinicalExpectations.emrSystem'] = {
                              value: emr,
                              timestamp
                            };
                            userInputTracker.current[index]['emrSystem'] = {
                              value: emr,
                              timestamp
                            };
                            
                            // Only update the root-level emrSystem in the API - this is what the backend expects
                            // No need to update clinicalExpectations.emrSystem in the API
                            updateFacilityFieldImmediately(index, 'emrSystem', emr);
                          }}
                        >
                          {emr}
                        </div>
                      ))}
                    </div>
                  </Form.Group>
                  
                  {/* <Form.Group className="field-group">
                    <Form.Label className="field-label">Website</Form.Label>
                    <Form.Control
                      value={facility.website || ''}
                      onChange={(e) => handleFacilityChange(index, 'website', e.target.value)}
                      placeholder="https://"
                      className="form-control"
                    />
                  </Form.Group> */}
                </div>
              </Accordion.Collapse>
            </Accordion>
          </div>
        ); // Closing return statement of map callback
      }) // Closing fields.map call
    )} {/* Closing ternary operator */}
  </div> // Closing "form-content" div
);
};

export default FacilityDetailsStep;
